// ============================================================================
// WorkbenchActions.jsx
// Actions list — accordion + compact table toggle.
// ============================================================================

import React, { useState, useMemo } from 'react';
import {
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Grid3X3,
  List,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  ACTION_TYPES, ACTION_STATUSES, SEVERITIES, VALUES, EFFORTS,
  normaliseAction, actionScore, statusTone,
} from '@process-advisor/process-miner/consultant/workbenchShared';
import * as S from '@process-advisor/process-miner/consultant/workbenchStyles';

// ── Shared tiny components ──────────────────────────────────────────────────

const StatusPill = ({ children, tone = 'neutral' }) => (
  <span style={S.statusPillStyle(tone)}>{children}</span>
);

const Select = ({ value, onChange, options }) => (
  <select style={S.SELECT} value={value || ''} onChange={(e) => onChange(e.target.value)}>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const Field = ({ label, value, onChange, placeholder = '', rows = 1, style = {} }) => (
  <label style={{ ...S.FIELD, ...style }}>
    <span style={S.FIELD_LABEL}>{label}</span>
    {rows > 1
      ? <textarea style={S.TEXTAREA} value={value || ''} placeholder={placeholder} rows={rows} onChange={(e) => onChange?.(e.target.value)} />
      : <input    style={S.INPUT}    value={value || ''} placeholder={placeholder}             onChange={(e) => onChange?.(e.target.value)} />
    }
  </label>
);

// ── Accordion row ───────────────────────────────────────────────────────────

const ActionAccordion = ({ action, expanded, onToggle, onUpdate, onRemove, linkedFinding }) => (
  <div style={S.LIST_ITEM}>
    <div
      style={S.LIST_SUMMARY}
      onClick={onToggle}
      onMouseEnter={(e) => { e.currentTarget.style.background = S.LIST_SUMMARY_HOVER.background; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
    >
      <div>{expanded ? <ChevronDown size={18} color={S.C.N300} /> : <ChevronRight size={18} color={S.C.N300} />}</div>
      <div style={{ minWidth: 0 }}>
        <div style={S.ITEM_TITLE}>{action.title || 'Untitled action'}</div>
        <div style={S.ITEM_SUBTITLE}>{linkedFinding ? `Linked to: ${linkedFinding.title || linkedFinding.finding}` : 'No linked finding'}</div>
      </div>
      <div style={S.CHIP_ROW}>
        <StatusPill tone="neutral">{action.actionType}</StatusPill>
        <StatusPill tone="neutral">{action.priority}</StatusPill>
        <StatusPill tone={statusTone(action.status)}>{action.status}</StatusPill>
      </div>
      <div style={S.ITEM_ACTIONS} onClick={(e) => e.stopPropagation()}>
        <button style={S.ICON_BUTTON} title="Remove action" onClick={onRemove}><Trash2 size={15} /></button>
      </div>
    </div>
    {expanded && (
      <div style={S.ITEM_BODY}>
        {linkedFinding && (
          <div style={S.LINKED_BADGE}>
            <strong>Linked finding:</strong> {linkedFinding.title || linkedFinding.finding}
          </div>
        )}
        <div style={S.ITEM_GRID_ACTION}>
          <Field label="Title" value={action.title || ''} onChange={(v) => onUpdate({ title: v })} />
          <label style={S.FIELD}><span style={S.FIELD_LABEL}>Action type</span><Select value={action.actionType} onChange={(v) => onUpdate({ actionType: v })} options={ACTION_TYPES} /></label>
          <label style={S.FIELD}><span style={S.FIELD_LABEL}>Status</span><Select value={action.status} onChange={(v) => onUpdate({ status: v })} options={ACTION_STATUSES} /></label>
          <label style={S.FIELD}><span style={S.FIELD_LABEL}>Priority</span><Select value={action.priority} onChange={(v) => onUpdate({ priority: v })} options={SEVERITIES} /></label>
        </div>
        <div style={S.FORM_GRID}>
          <label style={S.FIELD}><span style={S.FIELD_LABEL}>Expected value</span><Select value={action.expectedValue} onChange={(v) => onUpdate({ expectedValue: v })} options={VALUES} /></label>
          <label style={S.FIELD}><span style={S.FIELD_LABEL}>Effort</span><Select value={action.effort} onChange={(v) => onUpdate({ effort: v })} options={EFFORTS} /></label>
          <Field label="Owner" value={action.owner} onChange={(v) => onUpdate({ owner: v })} />
          <Field label="Target date" value={action.targetDate} onChange={(v) => onUpdate({ targetDate: v })} />
          <Field style={{ gridColumn: '1 / -1' }} label="Implementation steps" value={action.implementationSteps || ''} onChange={(v) => onUpdate({ implementationSteps: v })} rows={3} />
          <Field style={{ gridColumn: '1 / -1' }} label="Success measure" value={action.successMeasure || ''} onChange={(v) => onUpdate({ successMeasure: v })} rows={2} />
          <Field style={{ gridColumn: '1 / -1' }} label="Evidence summary" value={action.evidenceSummary || ''} onChange={(v) => onUpdate({ evidenceSummary: v })} rows={2} />
        </div>
      </div>
    )}
  </div>
);

// ── Table row ───────────────────────────────────────────────────────────────

const ActionTableRow = ({ action, linkedFinding, onSelect, onRemove }) => (
  <tr>
    <td style={{ ...S.TD, ...S.TD_TITLE }} onClick={onSelect}>{action.title || 'Untitled action'}</td>
    <td style={S.TD}>{action.actionType}</td>
    <td style={S.TD}>{linkedFinding ? linkedFinding.title : '-'}</td>
    <td style={S.TD}><StatusPill tone="neutral">{action.priority}</StatusPill></td>
    <td style={S.TD}><StatusPill tone={statusTone(action.status)}>{action.status}</StatusPill></td>
    <td style={S.TD}>
      <button style={S.ICON_BUTTON} title="Remove" onClick={onRemove}><Trash2 size={14} /></button>
    </td>
  </tr>
);

// ── Main component ──────────────────────────────────────────────────────────

const WorkbenchActions = ({
  workspace = {},
  onUpdateAction,
  onRemoveAction,
  onAddAction,
}) => {
  const [view, setView] = useState('list');
  const [expanded, setExpanded] = useState({});
  const findings = workspace.findings || [];
  const actions  = workspace.actions  || [];

  const sortedActions = useMemo(() =>
    [...actions].sort((a, b) => actionScore(b) - actionScore(a)),
    [actions]
  );

  const linkedFinding = (action) => findings.find(f => f.id === action.findingId);
  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={S.ROOT}>
      <div style={S.SHELL}>
        <div style={S.CARD}>

          <div style={S.CARD_HEADER}>
            <div style={S.CARD_TITLE_ROW}>
              <CheckSquare size={17} color={S.C.G400} />
              <div style={S.CARD_TITLE}>Actions</div>
              <span style={S.PILL}>{actions.length}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {actions.length > 0 && (
                <div style={{ display: 'flex', border: `1px solid ${S.C.N40}`, borderRadius: 8, overflow: 'hidden' }}>
                  <button
                    style={{ ...S.ICON_BUTTON, border: 'none', borderRadius: 0, background: view === 'list' ? S.C.B50 : S.C.N0 }}
                    title="Accordion view"
                    onClick={() => setView('list')}
                  ><List size={15} color={view === 'list' ? S.C.B400 : S.C.N300} /></button>
                  <button
                    style={{ ...S.ICON_BUTTON, border: 'none', borderRadius: 0, borderLeft: `1px solid ${S.C.N40}`, background: view === 'table' ? S.C.B50 : S.C.N0 }}
                    title="Table view"
                    onClick={() => setView('table')}
                  ><Grid3X3 size={15} color={view === 'table' ? S.C.B400 : S.C.N300} /></button>
                </div>
              )}
              <button style={S.BUTTON_SECONDARY} onClick={onAddAction}><Plus size={15} /> Add Action</button>
            </div>
          </div>

          <div style={S.CARD_BODY}>
            {actions.length === 0 && (
              <div style={S.EMPTY}>
                No actions yet. Convert a validated finding into an action, or create one manually.
              </div>
            )}

            {actions.length > 0 && view === 'list' && (
              <div style={S.LIST}>
                {sortedActions.map((a) => (
                  <ActionAccordion
                    key={a.id}
                    action={a}
                    expanded={!!expanded[a.id]}
                    onToggle={() => toggle(a.id)}
                    onUpdate={(patch) => onUpdateAction(a.id, patch)}
                    onRemove={() => onRemoveAction(a.id)}
                    linkedFinding={linkedFinding(a)}
                  />
                ))}
              </div>
            )}

            {actions.length > 0 && view === 'table' && (
              <div style={{ overflowX: 'auto' }}>
                <table style={S.TABLE}>
                  <thead>
                    <tr>
                      <th style={S.TH}>Action</th>
                      <th style={S.TH}>Type</th>
                      <th style={S.TH}>Linked finding</th>
                      <th style={S.TH}>Priority</th>
                      <th style={S.TH}>Status</th>
                      <th style={{ ...S.TH, width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedActions.map((a) => (
                      <ActionTableRow
                        key={a.id}
                        action={a}
                        linkedFinding={linkedFinding(a)}
                        onSelect={() => { setView('list'); setExpanded(prev => ({ ...prev, [a.id]: true })); }}
                        onRemove={() => onRemoveAction(a.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default WorkbenchActions;
